import { Track } from "@/src/lib/tracking";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";
import { QUERY_KEYS } from "@config/constant";
import Translations from "@translate/hooks/useSubscribe";
import { I18nUtil } from "@utils/i18n";

import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";

const I18n = I18nUtil.createScoped(Translations);

interface UseSubscribeProps {
  closePending?: () => void;
}

export const useSubscribe = (props?: UseSubscribeProps) => {
  const { closePending } = props || {};
  const { notifyApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();
  const [isLoading, setLoading] = useState(false);

  const { mutateAsync: bindToken, isPending: bindTokenLoading } = useMutation({
    mutationFn: async ({
      token,
      utc_offset_minutes,
    }: {
      token: string;
      utc_offset_minutes?: number;
    }) => {
      try {
        const response = await notifyApi?.device.bindDevice({
          signature: "",
          token,
          utc_offset_minutes,
        });
        return response;
      } catch (err) {
        Track.log("error", "useSubscribe", "bind error");
        setLoading(false);
        throw err;
      }
    },
    mutationKey: [QUERY_KEYS.BIND_DEVICE],
    onSuccess: (response) => {
      if (response?.data?.errors) {
        return handleFail(response?.data?.code?.toString());
      }
    },
    onError: (error) => {
      Track.log("error", "useSubscribe", `bindToken error:  ${error}`);
      setLoading(false);
      handleFail();
    },
  });

  // 开关状态枚举是OPEN,CLOSED
  // name：agent_agent
  const { mutateAsync: updateSubscription } = useMutation({
    mutationFn: async ({
      status,
      name = "agent_agent",
      utc_offset_minutes,
    }: {
      status: string;
      name?: string;
      utc_offset_minutes?: number;
    }) => {
      try {
        const response = await notifyApi?.setting.setting({
          name,
          status,
          utc_offset_minutes,
        });
        return response;
      } catch (err) {
        Track.error(err, { tag: "Settings", msg: "updateSubscription error" });
        setLoading(false);
        throw err;
      }
    },
    mutationKey: [QUERY_KEYS.UPDATE_SUBSCRIPTION],
    onSuccess: (response) => {
      if (response?.data?.errors) {
        return handleFail(response?.data?.code?.toString());
      }
      if (response?.data?.data) {
        handleSuccess();
      }
    },
    onError: (error) => {
      Track.log("error", "useSubscribe", `updateSubscription error:  ${error}`);
      setLoading(false);
      handleFail();
    },
  });

  const { mutateAsync: getPushSetting } = useMutation({
    mutationFn: async () => {
      try {
        const response = await notifyApi?.settings.listSettings();
        return response;
      } catch (err) {
        Track.log("error", "useSubscribe", "getPushSetting error");
        setLoading(false);
        throw err;
      }
    },
    mutationKey: [QUERY_KEYS.GET_PUSH_SETTING],
    onSuccess: (response) => {
      if (response?.data?.errors) {
        return handleFail(response?.data?.code?.toString());
      }
    },
    onError: (error) => {
      Track.log("error", "useSubscribe", `getPushSetting error:  ${error}`);
      setLoading(false);
      handleFail();
    },
  });

  const handleFail = (code?: string) => {
    setLoading(false);
    closePending?.();
    if (code === "1304") {
      OspToast.error({
        content: I18n.t("Oops...Something went wrong. Please try again."),
      });
    } else {
      OspToast.error({
        content: I18n.t("Fail to connect. Please try again."),
      });
    }
  };

  const handleSuccess = () => {
    setTimeout(() => {
      setLoading(false);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SUBSCRIBE_CHECK],
      });
      closePending?.();
    }, 1000);
  };

  return {
    bindToken,
    bindTokenLoading,
    updateSubscription,
    isLoading,
    getPushSetting,
  };
};
